import type { FC, ReactNode } from 'react';

import type { Icons } from '@opendoor/bricks-next';
import { Box, Col, Text, useMedia } from '@opendoor/bricks-next';
import type { GetProps } from '@tamagui/core';

import { PressableBox } from 'platform/bricks-next';
import type { PressableBoxProps } from 'platform/bricks-next';

export type NavTabColor = GetProps<typeof Icons.Sign>['color'];

type NavTabProps = Pick<PressableBoxProps, 'aria-label' | 'href'> & {
  /** Name used for metrics only. */
  name: string;
  label: string;
  icon?: (color: NavTabColor) => ReactNode;
  isActive: boolean;
};

/** Navigation tab that renders as an anchor with active state handling. */
export const NavTab: FC<NavTabProps> = ({
  name,
  label,
  icon,
  isActive,
  'aria-label': ariaLabel,
  ...restProps
}) => {
  const { smallerThanMD } = useMedia();
  const activeColor = smallerThanMD ? '$contentAccentBrand' : '$contentPrimary';
  const inactiveColor = smallerThanMD ? '$contentPrimary' : '$contentStateInactivePrimary';
  const color = isActive ? activeColor : inactiveColor;
  const hoverColor = smallerThanMD ? undefined : activeColor;

  return (
    <PressableBox
      display="flex"
      justifyContent="center"
      px="$4x"
      py="$2x"
      my="$4x"
      width={64}
      height={64}
      backgroundColor={isActive ? '$transparent' : undefined}
      $largerThanSM={{
        py: '$6x',
        m: 0,
        width: 'auto',
        height: 'auto',
        justifyContent: 'flex-start',
        borderRadius: '$8x',
      }}
      $largerThanMD={{ px: '$8x' }}
      hoverStyle={{
        backgroundColor: isActive || smallerThanMD ? '$transparent' : '$backgroundSecondary',
      }}
      group
      analyticsName={`cake-${name}-nav-tab`}
      aria-label={ariaLabel ?? ''}
      aria-current={isActive ? 'page' : undefined}
      data-testid={`${name}-nav-tab`}
      $platform-web={{ transition: 'all 0.3s ease' }}
      {...restProps}
    >
      <Box
        flexDirection="column"
        justifyContent="center"
        $largerThanSM={{ justifyContent: 'flex-start', flexDirection: 'row' }}
        alignItems="center"
      >
        {icon && (
          <Col mb="$2x" $largerThanSM={{ mb: 0, mr: '$10x' }}>
            {icon(color)}
          </Col>
        )}
        <Text
          tag="span"
          typography="$labelMedium"
          $group-hover={{ color: hoverColor }}
          $platform-web={{ transition: 'all 0.3s ease' }}
          $largerThanSM={{ typography: '$labelLarge' }}
          color={color}
        >
          {label}
        </Text>
      </Box>
    </PressableBox>
  );
};
